import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Heading, Loading } from '@orobix/orobix-fe-components'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import TrainingPropertiesList from '../../components/TrainingPropertiesList/TrainingPropertiesList'
import { useDispatch, useStore } from '../../store/store'
import { Training } from '../../store/store.types'
import {
  emptyTrainingTests,
  endpoints,
  fetchTraining,
  setTraining,
} from '../../store/storeActions'
import { get } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { locations } from '../../utils/locations'
import { isProductDisabled } from '../../utils/productTypes'
import useStyles from './TrainingDetails.styles'
import TrainingsNotes from './TrainingsNotes'
import TrainingsNotesUpdate from './TrainingsNotesUpdate'
import TrainingTestsList from './TrainingTestsList'

interface RouteParams {
  productId: string
  projectId: string
}

interface TrainingDetailsProps {
  trainingId: number
  detailsRefreshTrigger: number
}

const TrainingDetails = ({
  trainingId,
  detailsRefreshTrigger,
}: TrainingDetailsProps) => {
  const { t } = useTranslation()
  const { training, product } = useStore()
  const dispatch = useDispatch()
  const { productId, projectId } = useParams<RouteParams>()
  const classes = useStyles()
  const history = useHistory()
  const [trainingsNotesUpdateModal, setTrainingsNotesUpdateModal] = useState(
    false
  )
  const [notes, setNotes] = useState<string>('')

  useEffect(() => {
    if (!training) {
      fetchTraining(dispatch, trainingId)
    }
    setNotes(training?.notes || '')
    return () => {
      emptyTrainingTests(dispatch)
    }
  }, [dispatch, training, trainingId, training?.notes])

  useEffect(() => {
    get<Training>(endpoints.getTraining(trainingId))
      .then((data) => {
        setNotes(data.notes || '')
      })
      .catch((errors: AxiosError) => {
        handleApiErrors(errors)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsRefreshTrigger])

  return training && product ? (
    <>
      <div id="divTrainingDetails" className={classes.trainingDetails}>
        <div className={classes.trainingHead}>
          <div className={classes.trainingName}>
            <Heading headingLevel="2" noTopMargin noBottomMargin>
              {training.name}
            </Heading>
          </div>

          <div className={classes.buttons}>
            <Button
              onClick={() => {
                history.push(
                  locations.training(
                    Number(projectId),
                    Number(productId),
                    training.id
                  )
                )

                setTraining(dispatch, training)
              }}
              label={t('Details')}
              buttonType="button"
              type="action"
              size="sm"
            />

            <Button
              onClick={() =>
                history.push({
                  hash: '',
                  state: {
                    trainingId: undefined,
                  },
                })
              }
              label={t('Close')}
              hideLabel
              icon={<FontAwesomeIcon icon={faTimes} />}
              type="default"
              buttonType="button"
              size="sm"
            />
          </div>
        </div>

        <TrainingPropertiesList type="summary" />

        <TrainingsNotes
          notes={notes || ''}
          onClick={() => {
            setTrainingsNotesUpdateModal(true)
          }}
        />
      </div>

      {training.status === 'COMPLETED' && (
        <>
          <div className={classes.testsHeader}>
            <div className={classes.testsTitle}>
              <Heading headingLevel="3" noTopMargin noBottomMargin>
                {t('Tests')}
              </Heading>
            </div>

            <Button
              onClick={() => {
                history.push(
                  locations.trainingTestNew(
                    Number(projectId),
                    Number(productId),
                    training.id
                  )
                )

                setTraining(dispatch, training)
              }}
              label={t('New test')}
              buttonType="button"
              type="action"
              size="sm"
              disabled={isProductDisabled(product?.type)}
              title={
                isProductDisabled(product?.type)
                  ? t(
                      `You can't download the trained model with the current version of the software`
                    )
                  : undefined
              }
            />
          </div>
          <TrainingTestsList
            trainingId={training.id}
            productType={training.product.type}
          />
          {trainingsNotesUpdateModal && (
            <TrainingsNotesUpdate
              onClose={() => setTrainingsNotesUpdateModal(false)}
              onSuccess={() => {
                toast.success(t('The training has been edited'))
                setTrainingsNotesUpdateModal(false)
              }}
              trainingId={training.id}
              initialValues={{
                notes: notes || '',
              }}
              setNotes={setNotes}
            />
          )}
        </>
      )}
    </>
  ) : (
    <Loading />
  )
}

export default TrainingDetails
